<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { storeToRefs } from "pinia";
import PopularSearchBadge from "~/components/layout/Search/PopularSearchBadge.vue";
import Skeleton from "~/components/ui/Skeleton.vue";
import { useBetslip } from "~/store/betslip";
import { IEntityTop, IPopularSearches } from "~/types/graph-ql/graphql-types";
import t from "~/utils/translate";

defineProps<{ popularSearches: IPopularSearches | undefined; loadingPopularSearches: boolean }>();
const emit = defineEmits<(e: "select", value: IEntityTop | { name: string; id: string | null }) => void>();

const betSlip = useBetslip();
const { recentSearches } = storeToRefs(betSlip);

const removeRecentSearch = (recent: { name: string; id: string | null }) => {
	if (recent.id) {
		recentSearches.value = recentSearches.value.filter((search) => search.id !== recent.id);
	} else {
		recentSearches.value = recentSearches.value.filter((search) => search.name !== recent.name);
	}
};
</script>

<template>
	<div class="p-2 drop-shadow bg-light-50 dark:bg-dark-700 rounded-lg">
		<div v-if="recentSearches.length > 0">
			<div class="uppercase text-xs mb-2">
				{{ t("recent") }}
			</div>
			<div class="flex gap-2 flex-wrap mb-4">
				<div
					v-for="recentSearch in recentSearches"
					:key="recentSearch.name"
					class="px-2 py-1 bg-light-500 dark:bg-dark-800 rounded-full text-xs cursor-pointer hover:bg-light-300 transition flex justify-between items-center"
				>
					<span @click="emit('select', recentSearch)">
						{{ recentSearch.name }}
					</span>

					<UiIcon
						:path="mdiClose"
						class="fill-dark-300 h-[16px] w-[16px] ml-2 dark:fill-light-500"
						@click.stop="
							(e) => {
								e.preventDefault();
								removeRecentSearch(recentSearch);
							}
						"
					/>
				</div>
			</div>
		</div>
		<div>
			<div class="uppercase text-xs mb-2">
				{{ t("popular") }}
			</div>
			<div class="flex flex-wrap gap-2">
				<template v-if="loadingPopularSearches">
					<Skeleton v-for="n in 10" class="h-[32px] w-[calc(25%-0.5rem)] bg-light-500 dark:bg-dark-800 rounded-full transition" :key="n" />
				</template>
				<template
					v-if="!loadingPopularSearches && popularSearches && popularSearches.entity.top.length > 0"
					v-for="popularSearch in popularSearches.entity.top"
					:key="popularSearch.id"
				>
					<PopularSearchBadge :entity-top="popularSearch" @select="emit('select', popularSearch)"></PopularSearchBadge>
				</template>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss"></style>
